// Helpers shared by the student portal's Pathways and Institutions pages.
// Ported from transition_uni_listings (utils/pathwayLadder.js, utils/stateAbbr.js)
// so the portal cards resolve the same chips and stats as transitionu.health.

const STATE_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA',
  Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA', Maine: 'ME', Maryland: 'MD',
  Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN', Mississippi: 'MS', Missouri: 'MO',
  Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH',
  Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
  'District of Columbia': 'DC', 'District Of Columbia': 'DC',
};

export function stateAbbr(name) {
  if (!name) return '';
  const key = String(name).trim();
  return STATE_ABBR[key] || (key.length === 2 ? key.toUpperCase() : key);
}

// Chips carry canonical credential initials only, never program nicknames.
const CREDENTIAL_ALIASES = {
  'Rad Tech': 'RT(R)',
  RadTech: 'RT(R)',
  'Radiologic Technologist': 'RT(R)',
  'Med Aide': 'CMA',
  Sonographer: 'DMS',
};

export function canonicalCredential(code, title = '') {
  if (!code) return code;
  if (CREDENTIAL_ALIASES[code]) return CREDENTIAL_ALIASES[code];
  if (code === 'RT' && /respiratory/i.test(title)) return 'RRT';
  if (code === 'RT' && /radiolog/i.test(title)) return 'RT(R)';
  return code;
}

export function ladderStepsFromPathway(p) {
  const from = String((p && p.from_program && (p.from_program.abbr || p.from_program.short)) || '').trim();
  const to = String((p && p.to_program && (p.to_program.abbr || p.to_program.short)) || '').trim();
  if (from && to && from.toLowerCase() !== to.toLowerCase()) {
    return [{ code: from }, { code: to }];
  }
  const title = String((p && p.title) || '').trim();
  const match = title.match(/^(.{1,28}?)\s+(?:to|→|->)\s+(.{1,28})$/i);
  if (match) return [{ code: match[1].trim() }, { code: match[2].trim() }];
  return null;
}

export function pathwayEnds(p) {
  const steps = ladderStepsFromPathway(p);
  if (!steps || !steps.length) return null;
  const title = (p && p.title) || '';
  return {
    start: canonicalCredential(steps[0].code, title),
    end: canonicalCredential(steps[steps.length - 1].code, title),
  };
}

/** "2 to 3 years", "4 years", "12 to 18 months": the same label the public site shows. */
export function pathwayTime(p) {
  const v = Number(p && p.timeframe_value);
  const to = Number(p && p.timeframe_value_to);
  const u = String((p && p.timeframe_unit) || '');
  if (!v || !u) return '';
  const unit = n => {
    if (/month/i.test(u) && n % 12 === 0) {
      const y = n / 12;
      return y === 1 ? '1 year' : `${y} years`;
    }
    return `${n} ${u}`;
  };
  if (to && to > v) {
    if (/month/i.test(u) && v % 12 === 0 && to % 12 === 0) return `${v / 12} to ${to / 12} years`;
    return `${v} to ${to} ${u}`;
  }
  return unit(v);
}

export function personName(person) {
  if (!person) return '';
  return (person.name || [person.first_name, person.last_name].filter(Boolean).join(' ') || '').trim();
}

export function personInitials(person) {
  if (!person) return '';
  if (person.initials) return person.initials;
  const f = (person.first_name || '').trim();
  const l = (person.last_name || '').trim();
  return ((f[0] || '') + (l[0] || '')).toUpperCase();
}

/**
 * Programs a student has unlocked at a given university through pathway
 * enrollments: the (university, program) pairs stored on each enrollment's
 * uni_offered_programs. Returns { enrolledHere, programIds }.
 */
export function enrollmentAccessForUniversity(enrollments, universityId) {
  const uniId = Number(universityId);
  const programIds = new Set();
  let enrolledHere = false;
  (enrollments || []).forEach(enrollment => {
    (enrollment.uni_offered_programs || []).forEach(uop => {
      const u = uop && uop.university;
      if (u && Number(u.id) === uniId) {
        enrolledHere = true;
        const programId = uop.program && Number(uop.program.id);
        if (programId) programIds.add(programId);
      }
    });
  });
  return { enrolledHere, programIds };
}

// Program category for a pathway, used to pick its hero image (ported from TU pathwayCategory).
export function pathwayCategory(p) {
  const parts = [
    p && p.title,
    p && p.from_program && p.from_program.title,
    p && p.from_program && p.from_program.abbr,
    p && p.to_program && p.to_program.title,
    p && p.to_program && p.to_program.abbr,
  ];
  const haystack = parts.filter(Boolean).join(' ').toLowerCase();
  if (/dental|hygien/.test(haystack)) return 'dental-hygiene';
  if (/respirat|\brrt\b/.test(haystack)) return 'respiratory-therapy';
  if (/radio|x-?ray|imaging|\brad\b/.test(haystack)) return 'radiologic-technology';
  if (/sonograph|ultrasound|echocard/.test(haystack)) return 'sonography';
  if (/physical.?therap|\bpta\b|\bdpt\b/.test(haystack)) return 'physical-therapy';
  if (/public ?health|\bmph\b|epidem/.test(haystack)) return 'public-health';
  return 'nursing';
}

const PATHWAY_IMAGES = {
  nursing: require('@/assets/images/pathways/nursing.png'),
  'dental-hygiene': require('@/assets/images/pathways/dental-hygiene.png'),
  'respiratory-therapy': require('@/assets/images/pathways/respiratory-therapy.png'),
  'radiologic-technology': require('@/assets/images/pathways/radiologic-technology.png'),
  sonography: require('@/assets/images/pathways/sonography.png'),
  'physical-therapy': require('@/assets/images/pathways/physical-therapy.png'),
  'public-health': require('@/assets/images/pathways/public-health.png'),
};

export function pathwayImageFor(p) {
  return PATHWAY_IMAGES[pathwayCategory(p)] || PATHWAY_IMAGES.nursing;
}

export function salaryK(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? `$${Math.round(n / 1000)}k` : '';
}
